import React, { useState, useEffect, useRef } from 'react';
import useCurrency from '../../../../components/CurrencyProvider';
import {
  Box,
  Typography,
  IconButton,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Tooltip,
} from '@mui/material';
import { BarcodeInfo, IndentDetail } from '../Issue/Issue.helper';
import { ReactComponent as InfoCircleRed } from '../../../../assets/SVG/info-circle-red.svg';
import { ReactComponent as PrinterIcon } from '../../../../assets/SVG/printer (1).svg';
import { ReactComponent as ViewStockBarcode } from '../../../../assets/SVG/viewStockBarcode.svg';
import { ReactComponent as InventoryScan } from '../../../../assets/SVG/inventoryScan.svg';
import { ReactComponent as NoDataImage } from '../../../../assets/SVG/NoStockFound.svg';
import TableSkeleton from '../../../../components/common/TableSkeleton';
import StockQRPreview from '../../Stocks/StockQRPreview';
import { useTranslation } from 'react-i18next';
import { barcodeBatchInfo, BarcodeDataResponse, tableScrollSx } from '../../Common/inventory.helper';
import { useAlert } from '../../../../components/alertProvider';
import { useGenerateBarcode } from '../../Queries/UseInventoryQuries';
import ReusableReportComponent from '../../../../components/printReports/ReusableReportComponent';
import ReceiveReport from './ReceiveReport';
import IssueInformationPopup from '../Modals/IssueInformationPopup';
import { getButtonStates } from '../../../../utils/permissionService';
import styles from '../StockTransfer.module.css';


export interface Column<T> {
  label: string;
  field: keyof T;
}

interface InfoData {
  indentNoDate?: string;
  prNoDate?: string;
  issueNoDate?: string;
  issueRemark?: string;
  receiveRemark?: string;
  receivedNoDate?: string;
}

// Helper to convert Date or string to string
const toDateString = (value: string | Date | undefined): string => {
  if (!value) return '';
  if (value instanceof Date) return value.toISOString();
  return value;
};

export type ReusableReceiveListTableProps<T extends {
  id: number | string;
  indent_details?: IndentDetail[];
  indentNoDate?: string;
  prNoDate?: string;
  issueNoDate?: string | Date;
  issueRemark?: string;
  remark?: string;
  receivedNoDate?: string | Date;
}> = {
  columns: Array<Column<T>>;
  data: T[];
  page: number;
  rowsPerPage: number;
  totalCount: number;
  onPageChange: (newPage: number) => void;
  className?: string;
  onRowClick?: (row: T) => void;
  onRefetch?: () => void;
  isLoading?: boolean;
};

// ✅ Reusable InfoRow component for grid-based label-value pairs
const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <>
    <Typography variant="caption" sx={{ opacity: 0.8 }}>{label}</Typography>
    <Typography variant="caption" sx={{ textAlign: 'left' }}>
      <Typography component="span" fontWeight={600} fontSize='14px'>{value}</Typography>
    </Typography>
  </>
);

// ✅ Helper component to render quantity with UOM label
const QtyWithUom = ({ qty, uomLabel }: { qty: number | string; uomLabel?: string }) => (
  <>
    {qty}
    {uomLabel && <Typography component="span" sx={{ fontSize: '11px' }}> ({uomLabel})</Typography>}
  </>
);


export default function ReceiveListTable<T extends {
  id: number | string;
  indent_details?: IndentDetail[];
  indentNoDate?: string;
  prNoDate?: string;
  issueNoDate?: string | Date;
  issueRemark?: string;
  remark?: string;
  receivedNoDate?: string | Date;
}>({
  columns,
  data,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  className,
  onRowClick,
  onRefetch,
  isLoading = false,
}: ReusableReceiveListTableProps<T>) {
  const { formatCurrency } = useCurrency();
  const getRowKey = (row: T, index: number) => (row.id !== undefined ? String(row.id) : `${index}`);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [openBarcodePreview, setOpenBarcodePreview] = useState(false);
  const [selectedBarcodeData, setSelectedBarcodeData] = useState<BarcodeDataResponse[]>([]);
  const [showPrint, setShowPrint] = useState(false);
  const [selectedRowForPrint, setSelectedRowForPrint] = useState<T | null>(null);
  const [openInfo, setOpenInfo] = useState(false);
  const [infoData, setInfoData] = useState<InfoData | null>(null);
  // const { t: tStock } = useTranslation('stock_config');
  const { t: tStockTransfer } = useTranslation('stockTransfer_config');
  const { addAlert } = useAlert();
  const { canEdit, canAdd, canPrint } = getButtonStates('Admin', 'Stock Transfer', 'Receive');
  const { mutate: generateBarcode, data: generatedBarcodeData, isPending, error } = useGenerateBarcode();
  
  // Store onRefetch in a ref to avoid unnecessary effect re-runs
  const onRefetchRef = useRef(onRefetch);
  useEffect(() => {
    onRefetchRef.current = onRefetch;
  }, [onRefetch]);

  // Track if we've already shown alert for current request
  const hasShownAlertRef = useRef(false);

  const onBarcodeGenerateClick = (row: T, detail: IndentDetail) => {
    if (!detail?.item?.id) {
      addAlert(tStockTransfer('Item information is missing'), 'error');
      return;
    }

    if (!detail.batch?.batch_number || !detail.batch?.id) {
      addAlert(tStockTransfer('Batch information is missing'), 'error');
      return;
    }

    const rowWithStore = row as T & { to_store_id?: string | number };
    if (!rowWithStore.to_store_id) {
      addAlert(tStockTransfer('Store information is missing'), 'error');
      return;
    }

    const barcodeData: barcodeBatchInfo = {
      item_id: detail.item.id,
      item_name: detail.item.name,
      batch_code: detail.batch.batch_number,
      batch_id: detail.batch.id, 
      store_id: Number(rowWithStore.to_store_id), 
    };
    hasShownAlertRef.current = false; // Reset before new request
    generateBarcode(barcodeData);
  };

  // Handle barcode generation response
  useEffect(() => {
    if (hasShownAlertRef.current) return; // Skip if alert already shown

    if (error) {
      addAlert(tStockTransfer('Failed to generate QR code'), 'error');
      hasShownAlertRef.current = true;
      return;
    }

    if (generatedBarcodeData && !isPending) {
      addAlert(tStockTransfer('QR code generated successfully'), 'success');
      setSelectedBarcodeData([generatedBarcodeData]);
      setOpenBarcodePreview(true);
      onRefetchRef.current?.();
      hasShownAlertRef.current = true;
    }
  }, [error, isPending, generatedBarcodeData, addAlert, tStockTransfer]);

  const handleRowClick = (row: T, rowKey: string) => {
    setExpandedRow((prev) => (prev === rowKey ? null : rowKey));
    // eslint-disable-next-line
    onRowClick && onRowClick(row);
  };

  const handleClosePrint = () => {
    setShowPrint(false);
    setSelectedRowForPrint(null);
  };
  const handleBarcodeClick = (e: React.MouseEvent, barcode: BarcodeInfo) => {
    e.stopPropagation();
    if (barcode) {
      setSelectedBarcodeData([barcode]);
      setOpenBarcodePreview(true);
    }
  };

  // const handlePrintBarcode = (item: BarcodeDataResponse, quantity = 1) => {
  //   const printWindow = window.open('', '_blank', 'width=900,height=600');
  //   if (printWindow) {
  //     // Generate multiple images as per quantity
  //     const imagesHtml = Array.from({ length: quantity })
  //       .map(() => `<img src="${item.barcode_image_url}" alt="${tStock('Alt_Barcode')}" style="max-width:300px;margin-bottom:12px;" />`)
  //       .join('');
  
  //     printWindow.document.write(`
  //         <!DOCTYPE html>
  //         <html>
  //           <head>
  //             <title>${tStock('Print_Barcode_Title')}</title>
  //             <style>
  //               body { margin: 0; display: flex; flex-direction: column; justify-content: center; align-items: center; }
  //               img { max-width: 100%; max-height: 100%; }
  //             </style>
  //           </head>
  //           <body>
  //             ${imagesHtml}
  //             <script>
  //               window.onload = function() {
  //                 window.print();
  //               };
  //             </script>
  //           </body>
  //         </html>
  //       `);
  //     printWindow.document.close();
  //   } else {
  //     addAlert(tStock('Print_Window_Error'), 'error');
  //   }
  // };

  return (
    <Box py={1} className={className} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <TableContainer sx={{ borderRadius: '12px', flex: 1, minHeight: 0, overflow: 'auto', ...tableScrollSx }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={String(col.field)} sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.7)', fontSize: '15px', backgroundColor: '#F8F8F8', position: 'sticky', top: 0, zIndex: 3 }}>
                  {col.label}
                </TableCell>
              ))}
              <TableCell sx={{ backgroundColor: '#F8F8F8', position: 'sticky', top: 0, zIndex: 3 }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableSkeleton columnWidths={Array(columns.length + 1).fill(150)} rows={rowsPerPage} />
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + 1} sx={{ borderBottom: 0 }}>
                  <Box className={styles.noDataImageStyle}>
                    <NoDataImage style={{ width: '600px', height: '400px' }} />
                    <Typography sx={{ mt: 1, color: '#666', fontWeight: 500 }}>
                      {tStockTransfer('No data available')}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, i) => {
                const rowKey = getRowKey(row, i);
                const isExpanded = expandedRow === rowKey;
                return (
                  <React.Fragment key={rowKey}>
                    <TableRow
                      selected={isExpanded}
                      onClick={() => handleRowClick(row, rowKey)}
                      sx={{
                        color: '#CFD1D4',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: '#F8F8F8',
                        },
                      }}
                    >
                      {columns.map((col) => (
                        <TableCell key={String(col.field)}>
                          {(row[col.field] as unknown as React.ReactNode) ?? ''}
                        </TableCell>
                      ))}

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <IconButton size="small" onClick={(e) => {
                            e.stopPropagation();
                            const payload: InfoData = {
                              indentNoDate: row.indentNoDate || '',
                              prNoDate: row.prNoDate || '',
                              issueRemark: row?.issueRemark || '',
                              receiveRemark: row?.remark || '',
                              issueNoDate: toDateString(row?.issueNoDate),
                              receivedNoDate: toDateString(row?.receivedNoDate),
                            };
                            setInfoData(payload);
                            setOpenInfo(true);
                          }}>
                            <InfoCircleRed />
                          </IconButton>
                          {canPrint && (
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRowForPrint(row);
                                setShowPrint(true);
                              }}
                            >
                              <PrinterIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded row - accordion */}
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + 1}
                        sx={{ padding: 0, borderBottom: 0 }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ background: '#FFF6F3', padding: 2 }}>
                            <Typography fontSize={'13px'} fontWeight={600} mb={1}>
                              {tStockTransfer('Received Items List')}
                            </Typography>

                            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                              <TableHead sx={{ backgroundColor: '#FFFFFF' }}>
                                <TableRow>
                                  <TableCell sx={{ width: '20%' }}>{tStockTransfer('Item Code | Item Name')}</TableCell>
                                  <TableCell sx={{ width: '15%' }}>{tStockTransfer('Batch Code | Expiry Date')}</TableCell>
                                  <TableCell sx={{ width: '12%' }}>{tStockTransfer('Quantity | UOM')}</TableCell>
                                  <TableCell sx={{ width: '11%' }}>{tStockTransfer('SGST % | Amt.')}</TableCell>
                                  <TableCell sx={{ width: '11%' }}>{tStockTransfer('CGST % | Amt.')}</TableCell>
                                  <TableCell sx={{ width: '13%' }}>{tStockTransfer('Cost Amount')}</TableCell>
                                  <TableCell sx={{ width: '13%' }}>{tStockTransfer('MRP Amount')}</TableCell>
                                  <TableCell sx={{ width: '5%' }}>{tStockTransfer('Bar Code')}</TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {row.indent_details?.map((d: IndentDetail, idx: number) => {
                                  const batchCode = d.batch?.batch_number ?? '-';
                                  const expiryDate = d.batch?.expiry_date ?? '-';
                                  const indentQty = d.indent_qty ?? 0;
                                  const issueQty = d.issue_qty ?? 0;
                                  const receivedQty = d.received_qty ?? 0;
                                  const baseCost = Number(d.batch?.base_cost_price ?? d.base_cost_price ?? 0);
                                  const baseMrp = Number(d.batch?.base_mrp ?? d.base_mrp ?? 0);
                                  const totalCostAmount = d.total_base_cost_price;
                                  const totalMrpAmount = d.total_mrp_cost_price;

                                  return (
                                    <TableRow key={`row_${idx}_0`}>
                                      <TableCell>{`${d.item?.code ?? '-'} | ${d.item?.name_label ?? d.item?.name ?? '-'}`}</TableCell>
                                      <TableCell>
                                        <Box>
                                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {tStockTransfer('Batch Code')}: {batchCode}
                                          </Typography>
                                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                                            {tStockTransfer('Expiry Date')}: {expiryDate}
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                          <InfoRow label={`${tStockTransfer('Received')} :`} value={<QtyWithUom qty={receivedQty} uomLabel={d.uom_label} />} />
                                          <InfoRow label={`${tStockTransfer('Indent')} :`} value={<QtyWithUom qty={indentQty} uomLabel={d.indent_uom_label} />} />
                                          <InfoRow label={`${tStockTransfer('Issued')} :`} value={<QtyWithUom qty={issueQty} uomLabel={d.uom_label} />} />
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                          <InfoRow label={`${tStockTransfer('Percent')} :`} value={`${d.Taxes?.sgst?.percent ?? 0}%`} />
                                          <InfoRow label={`${tStockTransfer('Amount')} :`} value={formatCurrency(d.Taxes?.sgst?.amount ?? 0)} />
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                          <InfoRow label={`${tStockTransfer('Percent')} :`} value={`${d.Taxes?.cgst?.percent ?? 0}%`} />
                                          <InfoRow label={`${tStockTransfer('Amount')} :`} value={formatCurrency(d.Taxes?.cgst?.amount ?? 0)} />
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                          <InfoRow label={`${tStockTransfer('Base Cost')} :`} value={formatCurrency(baseCost)} />
                                          <InfoRow label={`${tStockTransfer('Total Cost Amount')} :`} value={formatCurrency(Number(totalCostAmount))} />
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, rowGap: 0.5, alignItems: 'center' }}>
                                          <InfoRow label={`${tStockTransfer('Base MRP')} :`} value={formatCurrency(baseMrp)} />
                                          <InfoRow label={`${tStockTransfer('Total MRP Amount')} :`} value={formatCurrency(Number(totalMrpAmount))} />
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        {d.barcode ? (
                                          <Tooltip title={tStockTransfer('View QR-code')} arrow>
                                            <IconButton
                                              size="small"
                                              onClick={(e) => handleBarcodeClick(e, d.barcode!)}
                                            >
                                              <ViewStockBarcode />
                                            </IconButton>
                                          </Tooltip>
                                        ) : (canEdit || canAdd) ? (
                                          <Tooltip title={tStockTransfer('Generate QR-code')} arrow>
                                            <IconButton
                                              size="small"
                                              onClick={() => onBarcodeGenerateClick(row, d)}
                                            >
                                              <InventoryScan />
                                            </IconButton>
                                          </Tooltip>
                                        ) : null}
                                      </TableCell>

                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} px={1} sx={{ flexShrink: 0, backgroundColor: '#fff', pt: 2 }}>
        <Typography fontSize={13} color="text.secondary">
          {tStockTransfer('Showing {{from}} to {{to}} of {{total}} entries', {
            from: totalCount === 0 ? 0 : page * rowsPerPage + 1,
            to: Math.min((page + 1) * rowsPerPage, totalCount),
            total: totalCount
          })}
        </Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <Pagination
            count={Math.ceil(totalCount / rowsPerPage) || 1}
            page={page + 1}
            onChange={(_, v) => onPageChange(v - 1)}
            shape="rounded"
          />
        </Box>
      </Box>
      <StockQRPreview
        open={openBarcodePreview}
        onClose={() => {
          setOpenBarcodePreview(false);
          setSelectedBarcodeData([]);
        }}
        QRResults={selectedBarcodeData}
        // onPrint={handlePrintBarcode}
      />

      <ReusableReportComponent
        open={showPrint}
        onClose={handleClosePrint}
        reportTitle={tStockTransfer('Receive_Report')}
      >
        <ReceiveReport data={selectedRowForPrint} />
      </ReusableReportComponent>
      
      <IssueInformationPopup
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        data={infoData || null}
        IsFromReceive={true}
      />
    </Box>
  );
}
